import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { StatusBadge } from "@/components/site/StatusBadge";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { JOB_TYPES, payLabel, whereLabel } from "@/lib/community";

const btn = "rounded-lg px-3 py-1.5 text-xs font-semibold";

function Actions({ status, onSet, id }) {
  return (
    <div className="flex flex-wrap gap-2">
      {status !== "approved" && <button onClick={() => onSet("approved")} className={`${btn} bg-emerald-600 text-white`} data-testid={`approve-${id}`}>Approve</button>}
      {status !== "declined" && <button onClick={() => onSet("declined")} className={`${btn} border border-rose-300 text-rose-700`} data-testid={`decline-${id}`}>Decline</button>}
    </div>
  );
}

function useAdmin(url) {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("pending");
  const load = () => api.get(url).then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (id, status) => api.patch(`${url}/${id}`, { status }).then(() => { toast.success(`Marked ${status}`); load(); }).catch((e) => toast.error(formatApiError(e)));
  const shown = (rows || []).filter((r) => filter === "all" || r.status === filter || (filter === "requests" && r.feature_request?.status === "requested"));
  return { rows, shown, filter, setFilter, set, load };
}

function Filter({ value, onChange, opts, testId }) {
  return <div className="flex gap-2 mb-4" data-testid={testId}>{opts.map((o) => <button key={o} onClick={() => onChange(o)} className={`${btn} ${value === o ? "bg-[#0B2A4A] text-white" : "bg-white border"}`} data-testid={`${testId}-${o}`}>{o[0].toUpperCase() + o.slice(1)}</button>)}</div>;
}

function Pin({ j, reload }) {
  const live = j.status === "approved" && j.featured_until && new Date(j.featured_until) > new Date();
  const pin = (weeks) => api.patch(`/admin/jobs/${j.id}/feature`, { weeks }).then(() => { toast.success(weeks ? `Pinned +${weeks} week(s)` : "Unpinned"); reload(); }).catch((e) => toast.error(formatApiError(e)));
  const req = j.feature_request?.status === "requested" ? j.feature_request : null;
  if (j.status !== "approved" && !req) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl bg-[#FFF7EE] p-3 text-xs" data-testid={`host-job-pin-${j.id}`}>
      {live ? <span className="font-semibold text-[#D96F28]" data-testid={`host-job-featured-${j.id}`}>Top job until {fmtDate(j.featured_until)}</span> : <span className="text-slate-600">Not featured</span>}
      {req && <span className="font-semibold text-[#0B2A4A]" data-testid={`host-job-feature-request-${j.id}`}>· Requested {req.weeks} week(s) · ${req.amount} (collect payment, then pin)</span>}
      {j.status === "approved" && [1, 2, 4].map((w) => <button key={w} onClick={() => pin(w)} className={`${btn} bg-[#F08A3C] text-white`} data-testid={`host-job-pin-${w}-${j.id}`}>{live ? "+" : "Pin "}{w}w</button>)}
      {live && <button onClick={() => pin(0)} className={`${btn} border`} data-testid={`host-job-unpin-${j.id}`}>Unpin</button>}
    </div>
  );
}

export function JobsAdmin() {
  const { rows, shown, filter, setFilter, set, load } = useAdmin("/admin/jobs");
  if (!rows) return null;
  const used = rows.filter((j) => j.status === "approved" && j.featured_until && new Date(j.featured_until) > new Date()).length;
  return (
    <div data-testid="host-jobs-admin">
      <p className="mb-3 text-sm text-slate-600" data-testid="host-jobs-top-slots">Top jobs: <strong>{used}/3</strong> spots used · $50 per week</p>
      <Filter value={filter} onChange={setFilter} opts={["pending", "requests", "approved", "declined", "closed", "all"]} testId="jobs-filter" />
      {!shown.length && <p className="text-slate-500" data-testid="host-jobs-empty">No jobs here.</p>}
      <div className="flex flex-col gap-3">{shown.map((j) => (
        <div key={j.id} className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-5" data-testid={`host-job-${j.id}`}>
          <div className="flex flex-wrap justify-between gap-3">
            <div><p className="font-semibold text-[#0B2A4A]">{j.title} · <span className="font-normal">{j.company}</span></p><p className="text-sm text-slate-600">{JOB_TYPES[j.job_type]} · {whereLabel(j)} · {payLabel(j)}</p></div>
            <div className="flex items-center gap-3"><StatusBadge status={j.status} /><Actions status={j.status} id={j.id} onSet={(s) => set(j.id, s)} /></div>
          </div>
          <p className="mt-3 text-sm text-slate-700 whitespace-pre-wrap line-clamp-6">{j.description}</p>
          <Pin j={j} reload={load} />
          <p className="mt-2 text-xs text-slate-500">Posted {fmtDate(j.created_at)} by <Link to={`/host/people/${encodeURIComponent(j.owner_email)}?tab=messages`} className="text-[#147C88] font-semibold" data-testid={`host-job-dm-${j.id}`}>{j.owner_email}</Link> · Contact {j.contact_name}, {j.contact_email}{j.contact_whatsapp && `, ${j.contact_whatsapp}`} · {j.application_count} applications{j.status === "approved" && <> · <Link to={`/jobs/${j.id}`} className="text-[#147C88] font-semibold">View</Link></>}</p>
        </div>
      ))}</div>
    </div>
  );
}

export function CommunityAdmin() {
  const { rows, shown, filter, setFilter, set } = useAdmin("/admin/community/members");
  if (!rows) return null;
  return (
    <div data-testid="host-community-admin">
      <Filter value={filter} onChange={setFilter} opts={["pending", "approved", "declined", "all"]} testId="members-filter" />
      {!shown.length && <p className="text-slate-500" data-testid="host-members-empty">No members here.</p>}
      <div className="grid md:grid-cols-2 gap-3">{shown.map((m) => (
        <div key={m.id} className="bg-white rounded-2xl border border-[#0B2A4A]/10 p-5 text-sm" data-testid={`host-member-${m.id}`}>
          <div className="flex justify-between gap-2"><p className="font-semibold text-[#0B2A4A]">{m.name} <Link to={`/host/people/${encodeURIComponent(m.email)}?tab=messages`} className="font-normal text-[#147C88]" data-testid={`host-member-dm-${m.id}`}>· {m.email}</Link></p><StatusBadge status={m.status} /></div>
          <dl className="mt-2 space-y-1 text-slate-700">
            <p><strong>Works from:</strong> {m.works_from}</p><p><strong>Does:</strong> {m.what_i_do}</p><p><strong>Earns by:</strong> {m.how_i_earn}</p>
            <p><strong>Offers:</strong> {m.offer_community}</p><p><strong>Purpose:</strong> {m.purpose}</p>{m.skills && <p><strong>Skills:</strong> {m.skills}</p>}{m.website && <p><strong>Website:</strong> {m.website}</p>}
          </dl>
          <div className="mt-3"><Actions status={m.status} id={m.id} onSet={(s) => set(m.id, s)} /></div>
        </div>
      ))}</div>
    </div>
  );
}
